/**
 * AuthService - Handles authentication, token management, and currency storage
 */

import { getCurrencyConfig, DEFAULT_CURRENCY } from "../config/currencyConfig";

interface DecodedToken {
  exp: number;
  iat?: number;
  encryptedToken?: string;
  [key: string]: any;
}

class AuthServiceClass {
  private static TOKEN_KEY = "token";
  private static EVENT_ID_KEY = "eventId";
  private static CURRENCY_KEY = "currency";
  private static DEFAULT_EVENT_ID = "event-1"; // Aviator game event ID (event-6 is Plinko)

  private readUserDetails(): Record<string, unknown> | null {
    for (const key of ["userDetails", "userDetail"]) {
      const storedValue = localStorage.getItem(key);
      if (!storedValue) continue;

      try {
        let parsed: unknown = JSON.parse(storedValue);
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
        if (parsed && typeof parsed === "object") {
          return parsed as Record<string, unknown>;
        }
      } catch {}
    }

    return null;
  }

  getUserDetails(): Record<string, unknown> | null {
    return this.readUserDetails();
  }

  getUsername(): string {
    const userDetails = this.readUserDetails();
    const username =
      userDetails?.username ??
      userDetails?.userName ??
      userDetails?.displayName ??
      userDetails?.name;

    return typeof username === "string" && username.trim()
      ? username.trim()
      : "Player";
  }

  getUserId(): string | null {
    const userDetails = this.readUserDetails();
    const userId = userDetails?._id ?? userDetails?.id ?? userDetails?.userId;
    return userId === undefined || userId === null ? null : String(userId);
  }

  /**
   * Decode JWT token without verification (client-side only)
   */
  decodeToken(token: string): DecodedToken | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = JSON.parse(atob(payload));
      return decoded;
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const now = Date.now() / 1000;
    return decoded.exp <= now;
  }

  /**
   * Store token in localStorage
   */
  setToken(token: string): void {
    const normalizedToken = token.trim().replace(/^Bearer\s+/i, "");
    localStorage.setItem(AuthServiceClass.TOKEN_KEY, normalizedToken);
  }

  /**
   * Get token from localStorage
   */
  getToken(): string | null {
    const token = localStorage.getItem(AuthServiceClass.TOKEN_KEY);
    return token?.trim().replace(/^Bearer\s+/i, "") || null;
  }

  /**
   * Remove token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(AuthServiceClass.TOKEN_KEY);
  }

  /**
   * Store event ID in localStorage
   */
  setEventId(eventId: string): void {
    localStorage.setItem(AuthServiceClass.EVENT_ID_KEY, eventId);
  }

  /**
   * Get event ID from localStorage (defaults to event-1 for Aviator)
   */
  getEventId(): string {
    return localStorage.getItem(AuthServiceClass.EVENT_ID_KEY) || AuthServiceClass.DEFAULT_EVENT_ID;
  }

  /**
   * Remove event ID from localStorage
   */
  removeEventId(): void {
    localStorage.removeItem(AuthServiceClass.EVENT_ID_KEY);
  }

  /**
   * Store currency in localStorage
   */
  setCurrency(currency: string): void {
    const upperCurrency = currency?.trim().toUpperCase() || DEFAULT_CURRENCY;
    localStorage.setItem(AuthServiceClass.CURRENCY_KEY, upperCurrency);
  }

  /**
   * Get currency from localStorage
   */
  getCurrency(): string {
    return localStorage.getItem(AuthServiceClass.CURRENCY_KEY) || DEFAULT_CURRENCY;
  }

  /**
   * Get currency config for current currency
   */
  getCurrencyConfig() {
    return getCurrencyConfig(this.getCurrency());
  }

  /**
   * Remove currency from localStorage
   */
  removeCurrency(): void {
    localStorage.removeItem(AuthServiceClass.CURRENCY_KEY);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  }

  /**
   * Clear all auth data
   */
  clearAll(): void {
    this.removeToken();
    this.removeEventId();
    this.removeCurrency();
  }

  /**
   * Initialize auth from URL (used by AuthWrapper)
   * Returns false if no valid auth found
   */
  initializeFromUrl(pathname: string, search: string): {
    success: boolean;
    token?: string;
    eventId?: string;
    currency?: string;
    error?: string;
  } {
    // Extract eventId, token, and optional currency from URL path
    // Supports multiple formats:
    // 1. /auth/event-6/token?currency=PKR (query param with ?)
    // 2. /auth/event-6/token/PKR (path param - just currency code)
    // 3. /auth/event-6/token/currency=PKR (path param with currency=)
    const fullPathMatch = pathname.match(
      /\/auth\/event-(\d+|[a-zA-Z0-9_-]+)\/([^/?]+)(?:\/(?:currency=)?([A-Z]{3}))?/
    );

    const eventIdRaw = fullPathMatch?.[1] || null;
    const tokenFromPath = fullPathMatch?.[2] || null;
    const token = tokenFromPath
      ? decodeURIComponent(tokenFromPath).trim().replace(/^Bearer\s+/i, "")
      : null;
    const currencyFromPath = fullPathMatch?.[3] || null;

    // Construct full event ID (add 'event-' prefix)
    const eventId = eventIdRaw ? `event-${eventIdRaw}` : null;

    // Extract currency from query params or path, default to USD
    const searchParams = new URLSearchParams(search);
    const currencyFromQuery = searchParams.get("currency");
    const currency = currencyFromPath || currencyFromQuery || DEFAULT_CURRENCY;

    if (!token) {
      return { success: false, error: "No token provided in URL" };
    }

    // Validate token
    const decoded = this.decodeToken(token);
    if (!decoded) {
      return { success: false, error: "Invalid token format" };
    }

    // Check expiration
    if (this.isTokenExpired(token)) {
      const expiredDate = decoded.exp
        ? new Date(decoded.exp * 1000).toLocaleString()
        : "Unknown";
      return {
        success: false,
        error: `Token expired on ${expiredDate}. Please login again.`,
      };
    }

    return {
      success: true,
      token,
      eventId: eventId || undefined,
      currency: currency.toUpperCase(),
    };
  }
}

// Export singleton instance
export const AuthService = new AuthServiceClass();
